import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { ExportDropdown } from '../../components/ui/ExportDropdown'
import { FULFILLMENT_LABELS, StatusPlata } from '../../components/shopify/StatusPlata'
import { ShopifyDashboardWidget } from '../../components/ShopifyDashboardWidget'

type Segment = 'VIP' | 'recurrent' | 'active' | 'at_risk' | 'reactivation' | 'churned' | 'one_time'

interface Customer {
  id: number
  shopifyId: string
  email: string | null
  firstName: string | null
  lastName: string | null
  phone: string | null
  firstOrderAt: string | null
  lastOrderAt: string | null
  ordersCount: number
  totalSpent: string
  avgOrderValue: string
  segment: string | null
  cohortMonth: string | null
  daysSinceLastOrder: number | null
  city: string | null
  province: string | null
}

interface CustomerListResp {
  customers: Customer[]
  total: number
}

interface SegmentStat {
  segment: string
  count: number
  totalSpent: string
  avgLtv: string
}

interface SegmentsResp {
  segments: SegmentStat[]
  knownSegments: Segment[]
}

interface OrderRow {
  id: number
  shopifyId: string
  name: string
  totalPrice: string
  currency: string
  financialStatus: string | null
  fulfillmentStatus: string | null
  createdAtShopify: string
  cancelledAt: string | null
  motivNevandut: string | null
  utmSource: string | null
  utmCampaign: string | null
  shippingCity: string | null
}

interface DetailResp {
  customer: Customer
  orders: OrderRow[]
  topProducts: Array<{ sku: string | null; title: string; qty: number; revenue: string }>
  vouchers: Array<{ code: string; type: string; uses: number; totalAmount: string }>
}

export const SEGMENT_LABELS: Record<string, string> = {
  VIP: 'VIP',
  recurrent: 'Recurenți',
  active: 'Activi',
  at_risk: 'În risc',
  reactivation: 'Reactivare',
  churned: 'Pierduți',
  one_time: 'O singură comandă',
  unknown: 'Nesegmentat',
}

const SEGMENT_COLORS: Record<string, string> = {
  VIP: 'bg-b4l-purple-light text-b4l-purple-dark',
  recurrent: 'bg-b4l-green-light text-b4l-green-dark',
  active: 'bg-b4l-blue-light text-b4l-blue-dark',
  at_risk: 'bg-b4l-amber-light text-b4l-amber-dark',
  reactivation: 'bg-b4l-amber-light text-b4l-amber-dark',
  churned: 'bg-b4l-red-light text-b4l-red-dark',
  one_time: 'bg-b4l-bg text-b4l-text-muted',
  unknown: 'bg-b4l-bg text-b4l-text-muted',
}

export function ClientiShopifyPage() {
  /** Exportul e un drept PERSONAL: nu vine cu niciun rol, nici cu `admin`. */
  const poateExporta = useAuthStore((s) => s.permissions).includes('date.clienti:export')
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.shopify:write')

  const [search, setSearch] = useState('')
  const [segmentFilter, setSegmentFilter] = useState<string>('')
  const [cityFilter, setCityFilter] = useState('')
  const [minOrders, setMinOrders] = useState('')
  type SortCol =
    | 'totalSpent'
    | 'ordersCount'
    | 'avgOrderValue'
    | 'lastOrderAt'
    | 'city'
    | 'cohortMonth'
    | 'firstName'
  const [sort, setSort] = useState<SortCol>('totalSpent')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(10)
  const [detailId, setDetailId] = useState<number | null>(null)

  const onHeaderClick = (col: SortCol) => {
    if (sort === col) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSort(col)
      // Default direction per column: numeric/date desc, text asc
      setSortOrder(col === 'city' || col === 'firstName' ? 'asc' : 'desc')
    }
    setPage(0)
  }
  const sortIcon = (col: SortCol) => {
    if (sort !== col) return <span className="text-b4l-text-muted/50">↕</span>
    return <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
  }

  const segmentsQuery = useQuery<SegmentsResp>({
    queryKey: ['shopify-segments'],
    queryFn: async () => (await api.get<SegmentsResp>('/shopify/customers/segments')).data,
  })

  const listQuery = useQuery<CustomerListResp>({
    queryKey: [
      'shopify-customers',
      { search, segmentFilter, cityFilter, minOrders, sort, sortOrder, page, pageSize },
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        limit: String(pageSize),
        offset: String(page * pageSize),
        sort,
        sortOrder,
      }
      if (search.trim()) params.search = search.trim()
      if (segmentFilter) params.segment = segmentFilter
      if (cityFilter.trim()) params.city = cityFilter.trim()
      if (minOrders) params.minOrders = minOrders
      const qs = new URLSearchParams(params).toString()
      return (await api.get<CustomerListResp>(`/shopify/customers?${qs}`)).data
    },
  })

  const detailQuery = useQuery<DetailResp>({
    queryKey: ['shopify-customer', detailId],
    queryFn: async () => (await api.get<DetailResp>(`/shopify/customers/${detailId}`)).data,
    enabled: detailId !== null,
  })

  const recomputeMutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ updated: number }>('/shopify/customers/recompute-segments')).data,
    onSuccess: (d) => {
      toast.success(`Segmente recalculate (${d.updated} clienți)`)
      queryClient.invalidateQueries({ queryKey: ['shopify-segments'] })
      queryClient.invalidateQueries({ queryKey: ['shopify-customers'] })
    },
    onError: () => toast.error('Eroare la recalculare'),
  })

  const total = listQuery.data?.total ?? 0
  const visibleCount = listQuery.data?.customers.length ?? 0

  const [exporting, setExporting] = useState(false)
  const exportCustomers = async (currentPageOnly: boolean) => {
    try {
      setExporting(true)
      const exportParams = new URLSearchParams()
      if (search.trim()) exportParams.set('search', search.trim())
      if (segmentFilter) exportParams.set('segment', segmentFilter)
      if (cityFilter.trim()) exportParams.set('city', cityFilter.trim())
      if (minOrders) exportParams.set('minOrders', minOrders)
      if (currentPageOnly) {
        exportParams.set('limit', String(pageSize))
        exportParams.set('offset', String(page * pageSize))
      }
      await downloadXlsx(`/shopify/exports/customers?${exportParams.toString()}`)
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Clienți Shopify</h1>
        <div className="flex gap-2">
          {/*
          Butonul dispare pentru cine n-are dreptul. Nu e protectie — aceea e pe ruta, care refuza
          oricum — ci politete: sa nu apesi ceva ce vine inapoi cu „interzis".
        */}
          {poateExporta && (
            <ExportDropdown
              loading={exporting}
              pageCount={visibleCount}
              totalCount={total}
              onExportPage={() => exportCustomers(true)}
              onExportAll={() => exportCustomers(false)}
            />
          )}
          {canWrite && (
            <Button
              variant="secondary"
              onClick={() => recomputeMutation.mutate()}
              loading={recomputeMutation.isPending}
            >
              Recalculează segmente
            </Button>
          )}
        </div>
      </div>

      {/* Tabloul Shopify (clienti, VIP, venituri, orase) — mutat aici de pe vechiul Dashboard
          (14.10.2026): pagina de start arata ce e de facut, analiza sta in modulul ei. */}
      <ShopifyDashboardWidget />

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7">
        {(segmentsQuery.data?.segments ?? []).map((s) => (
          <button
            key={s.segment}
            onClick={() => {
              setSegmentFilter(segmentFilter === s.segment ? '' : s.segment)
              setPage(0)
            }}
            className={`rounded border p-2 text-left transition ${
              segmentFilter === s.segment
                ? 'border-b4l-green bg-b4l-green-light/30'
                : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
            }`}
          >
            <div
              className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                SEGMENT_COLORS[s.segment] ?? SEGMENT_COLORS.unknown
              }`}
            >
              {SEGMENT_LABELS[s.segment] ?? s.segment}
            </div>
            <div className="mt-1 text-lg font-semibold">{s.count}</div>
            <div className="text-[10px] text-b4l-text-muted">
              LTV mediu {Number(s.avgLtv).toFixed(0)} RON
            </div>
          </button>
        ))}
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <Field
            label="Căutare"
            placeholder="Email, nume, telefon"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
          />
          <Field
            label="Oraș"
            placeholder="ex: București"
            value={cityFilter}
            onChange={(e) => {
              setCityFilter(e.target.value)
              setPage(0)
            }}
          />
          <Field
            label="Comenzi ≥"
            type="number"
            placeholder="ex: 3"
            value={minOrders}
            onChange={(e) => {
              setMinOrders(e.target.value)
              setPage(0)
            }}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Segment</label>
            <select
              value={segmentFilter}
              onChange={(e) => {
                setSegmentFilter(e.target.value)
                setPage(0)
              }}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="">Toate</option>
              {(segmentsQuery.data?.knownSegments ?? []).map((s) => (
                <option key={s} value={s}>
                  {SEGMENT_LABELS[s] ?? s}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                <SortableTh
                  col="firstName"
                  sort={sort}
                  onClick={onHeaderClick}
                  icon={sortIcon('firstName')}
                >
                  Client
                </SortableTh>
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">Telefon</th>
                <th className="px-2 py-2">Segment</th>
                <SortableTh
                  col="ordersCount"
                  sort={sort}
                  onClick={onHeaderClick}
                  icon={sortIcon('ordersCount')}
                  align="right"
                >
                  Comenzi
                </SortableTh>
                <SortableTh
                  col="totalSpent"
                  sort={sort}
                  onClick={onHeaderClick}
                  icon={sortIcon('totalSpent')}
                  align="right"
                >
                  LTV (RON)
                </SortableTh>
                <SortableTh
                  col="avgOrderValue"
                  sort={sort}
                  onClick={onHeaderClick}
                  icon={sortIcon('avgOrderValue')}
                  align="right"
                >
                  AOV (RON)
                </SortableTh>
                <SortableTh
                  col="lastOrderAt"
                  sort={sort}
                  onClick={onHeaderClick}
                  icon={sortIcon('lastOrderAt')}
                >
                  Ultima
                </SortableTh>
                <SortableTh col="city" sort={sort} onClick={onHeaderClick} icon={sortIcon('city')}>
                  Oraș
                </SortableTh>
                <SortableTh
                  col="cohortMonth"
                  sort={sort}
                  onClick={onHeaderClick}
                  icon={sortIcon('cohortMonth')}
                >
                  Cohort
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={10} className="px-2 py-6 text-center text-b4l-text-muted">
                    Se încarcă...
                  </td>
                </tr>
              )}
              {listQuery.data?.customers.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-2 py-6 text-center text-b4l-text-muted">
                    Niciun client găsit
                  </td>
                </tr>
              )}
              {listQuery.data?.customers.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setDetailId(c.id)}
                  className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                >
                  <td className="px-2 py-2">
                    <div className="font-medium">
                      {c.firstName || c.lastName
                        ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
                        : '(fără nume)'}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-xs">{c.email ?? '-'}</td>
                  <td className="px-2 py-2 text-xs font-mono">{c.phone ?? '-'}</td>
                  <td className="px-2 py-2">
                    {c.segment && (
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          SEGMENT_COLORS[c.segment] ?? SEGMENT_COLORS.unknown
                        }`}
                      >
                        {SEGMENT_LABELS[c.segment] ?? c.segment}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">{c.ordersCount}</td>
                  <td className="px-2 py-2 text-right">{Number(c.totalSpent).toFixed(2)}</td>
                  <td className="px-2 py-2 text-right">{Number(c.avgOrderValue).toFixed(2)}</td>
                  <td className="px-2 py-2 text-xs">
                    {c.lastOrderAt ? new Date(c.lastOrderAt).toLocaleDateString('en-GB') : '-'}
                    {c.daysSinceLastOrder !== null && (
                      <span className="ml-1 text-b4l-text-muted">({c.daysSinceLastOrder}z)</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs">{c.city ?? '-'}</td>
                  <td className="px-2 py-2 text-xs">{c.cohortMonth ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(0)
            }}
            itemLabel="clienți"
          />
        </div>
      </Card>

      <Modal
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        title="Detalii client"
        size="xl"
      >
        {detailQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {detailQuery.data && <CustomerDetail data={detailQuery.data} />}
      </Modal>
    </div>
  )
}

function CustomerDetail({ data }: { data: DetailResp }) {
  const { customer, orders, topProducts, vouchers } = data
  const fullName =
    customer.firstName || customer.lastName
      ? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim()
      : (customer.email ?? '(fără nume)')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{fullName}</div>
          <div className="text-xs text-b4l-text-muted">
            {customer.email} {customer.phone ? `· ${customer.phone}` : ''}
          </div>
          <div className="text-xs text-b4l-text-muted">
            {customer.city}
            {customer.province ? `, ${customer.province}` : ''}
          </div>
        </div>
        {customer.segment && (
          <span
            className={`rounded px-2 py-1 text-xs font-semibold ${
              SEGMENT_COLORS[customer.segment] ?? SEGMENT_COLORS.unknown
            }`}
          >
            {SEGMENT_LABELS[customer.segment] ?? customer.segment}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Comenzi" value={customer.ordersCount} />
        <Kpi label="LTV (RON)" value={Number(customer.totalSpent).toFixed(2)} />
        <Kpi label="AOV (RON)" value={Number(customer.avgOrderValue).toFixed(2)} />
        <Kpi label="Zile de la ultima" value={customer.daysSinceLastOrder ?? '-'} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
            Top 5 produse
          </div>
          {topProducts.length === 0 && <div className="text-xs text-b4l-text-muted">Fără date</div>}
          <ul className="space-y-1 text-xs">
            {topProducts.map((p, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="truncate">
                  {p.sku ? `[${p.sku}] ` : ''}
                  {p.title}
                </span>
                <span className="whitespace-nowrap text-b4l-text-muted">
                  {p.qty}× · {Number(p.revenue).toFixed(0)} RON
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
            Vouchere folosite
          </div>
          {vouchers.length === 0 && <div className="text-xs text-b4l-text-muted">Niciunul</div>}
          <ul className="space-y-1 text-xs">
            {vouchers.map((v, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="font-mono">{v.code}</span>
                <span className="text-b4l-text-muted">
                  {v.uses}× · {Number(v.totalAmount).toFixed(0)} RON discount
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
          Ultimele {orders.length} ordere
        </div>
        <div className="overflow-x-auto rounded border border-b4l-border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-b4l-border bg-b4l-bg-soft text-left">
                <th className="px-2 py-1.5">Comandă</th>
                <th className="px-2 py-1.5">Data</th>
                <th className="px-2 py-1.5 text-right">Total</th>
                <th className="px-2 py-1.5">Stare plată</th>
                <th className="px-2 py-1.5">Expediere</th>
                <th className="px-2 py-1.5">UTM</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className={`border-b border-b4l-border ${
                    // Anulata, nelivrata sau returnata: nu e vanzare si nu intra in LTV de mai sus.
                    o.cancelledAt || o.motivNevandut ? 'text-b4l-text-muted line-through' : ''
                  }`}
                >
                  <td className="px-2 py-1.5 font-mono">{o.name}</td>
                  <td className="px-2 py-1.5">
                    {new Date(o.createdAtShopify).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {Number(o.totalPrice).toFixed(2)} {o.currency}
                  </td>
                  <td className="px-2 py-1.5">
                    <StatusPlata
                      financialStatus={o.financialStatus}
                      motivNevandut={o.motivNevandut}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    {FULFILLMENT_LABELS[o.fulfillmentStatus ?? 'unfulfilled'] ??
                      o.fulfillmentStatus}
                  </td>
                  <td className="px-2 py-1.5 text-b4l-text-muted">
                    {o.utmSource ? `${o.utmSource}/${o.utmCampaign ?? '-'}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded border border-b4l-border bg-b4l-bg-soft p-2">
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  )
}

function SortableTh<C extends string>({
  col,
  sort,
  onClick,
  icon,
  align = 'left',
  children,
}: {
  col: C
  sort: C
  onClick: (col: C) => void
  icon: React.ReactNode
  align?: 'left' | 'right'
  children: React.ReactNode
}) {
  return (
    <th
      onClick={() => onClick(col)}
      className={`cursor-pointer select-none px-2 py-2 hover:text-b4l-text ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${sort === col ? 'text-b4l-text' : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {icon}
      </span>
    </th>
  )
}
